import { ReactNode, useState, type MouseEvent } from 'react'
import { Box } from '@mui/material'
import { Link } from 'react-router-dom'
import { COLOR_PRIMARY, COLOR_SKY, FS_MD, TEXT_BUTTON } from '../common/theme'

interface FancyButtonProps {
    // Routing - pass exactly one of `to` (react-router Link), `href` (external),
    // or neither (renders as a <button> with onClick).
    to?: string
    href?: string
    onClick?: () => void
    // Gold-filled (solid) vs outlined / light treatment. Defaults to outlined.
    solid?: boolean
    // Tint colour. For solid this is the fill; for outlined it's the border.
    color?: string
    fullWidth?: boolean
    width?: string | number
    // Optional leading / trailing icon. Render anywhere a ReactNode is valid
    // (MUI icons, react-icons, plain SVG).
    startIcon?: ReactNode
    endIcon?: ReactNode
    disabled?: boolean
    // Native <button type>. Defaults to 'button' so dropping a FancyButton
    // inside a <form> doesn't accidentally submit it; pass 'submit' for the
    // primary CTA of an HTML form.
    type?: 'button' | 'submit' | 'reset'
    // Outlined-only knobs.
    transparent?: boolean
    noBorder?: boolean
    textColor?: string
    testId?: string
    children: ReactNode
}

// Single button used everywhere. `solid` flips between the old gold fill
// (with ripple) and the outlined light treatment. Both share the gold
// underline-reveal on hover.
export function FancyButton({
    to, href, onClick,
    solid = false,
    color = COLOR_PRIMARY,
    fullWidth, width, disabled,
    type = 'button',
    transparent,
    noBorder = true,
    testId,
    startIcon,
    endIcon,
    children,
}: FancyButtonProps) {
    const [hover, setHover] = useState(false)
    const [ripple, setRipple] = useState({ x: 0, y: 0 })

    const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
        if (disabled) return
        if (solid) {
            const rect = e.currentTarget.getBoundingClientRect()
            setRipple({ x: e.clientX - rect.left, y: e.clientY - rect.top })
        }
        setHover(true)
    }

    // const isSm = size === 'sm'
    const baseSx = {
        ...(fullWidth && { width: '100%' }),
        ...(width !== undefined && { width }),
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // `sm` is the compact in-app size used by dialog footers / toolbars;
        // `md` is the chunky marketing-page default.
        // px: isSm ? 1.5 : 3,
        // py: isSm ? 0.4 : (solid ? 1 : 1.25),
        // borderRadius: isSm ? '6px' : '8px',
        // fontSize: isSm ? '0.78rem' : '15px',
        px: 3,
        py: (solid ? 1 : 1.25),
        // Strip the UA default `<button>` border so disabled solid buttons
        // don't render the browser's grey outline. Outlined variants
        // override this in `outlinedSx` when `noBorder` is false.
        border: 'none',
        borderRadius: '8px',
        fontSize: FS_MD,
        fontWeight: 500,
        lineHeight: 1.5,
        textDecoration: 'none',
        textTransform: 'uppercase',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'color 0.3s ease',
        letterSpacing: '0.3px',
        userSelect: 'none',
        pointerEvents: disabled ? 'none' : 'auto',
        '&:hover .fancylink::after': disabled ? {} : {
            transform: 'scaleX(1)',
            transformOrigin: 'left',
        },
    }

    const solidSx = {
        ...baseSx,
        border: `2px solid ${disabled ? 'rgba(0,0,0,0)' : color}`,
        backgroundColor: disabled ? 'rgba(0,0,0,0.12)' : color,
        color: disabled ? 'rgba(0,0,0,0.26)' : hover ? COLOR_SKY : TEXT_BUTTON,
    }

    const outlinedSx = {
        ...baseSx,
        border: noBorder ? 'none' : `2px solid ${color}`,
        backgroundColor: transparent ? 'transparent' : 'white',
        color: hover ? COLOR_SKY : color,
    }

    const boxSx = solid ? solidSx : outlinedSx

    const linkSx = {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Inherit the parent button's color so the hover→sky-blue transition
        // (set in solidSx / outlinedSx) flows through to the label.
        color: 'inherit',
        userSelect: 'none',
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: '-4px',
            width: '100%',
            height: '2px',
            background: COLOR_PRIMARY,
            transform: 'scaleX(0)',
            transformOrigin: 'right',
            transition: 'transform 250ms ease-in',
        },
    }

    const inner = (
        <>
            {solid && (
                <Box
                    component="span"
                    sx={{ position: 'absolute', borderRadius: '50%', background: 'white', pointerEvents: 'none' }}
                    style={{
                        width: hover ? '1200px' : '0px',
                        height: hover ? '1200px' : '0px',
                        left: ripple.x,
                        top: ripple.y,
                        transform: 'translate(-50%, -50%)',
                        transition: 'width 0.4s ease-in-out, height 0.4s ease-in-out',
                    }}
                />
            )}
            <Box component="span" className="fancylink" sx={linkSx}>
                {startIcon && <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{startIcon}</Box>}
                {children}
                {endIcon && <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{endIcon}</Box>}
            </Box>
        </>
    )

    const onLeave = () => setHover(false)

    if (href) {
        return (
            <Box component="a" href={href} data-testid={testId} className="fancybutton" onMouseEnter={handleMouseEnter} onMouseLeave={onLeave} sx={boxSx}>
                {inner}
            </Box>
        )
    }
    if (to) {
        return (
            <Box component={Link} to={to} data-testid={testId} className="fancybutton" onMouseEnter={handleMouseEnter} onMouseLeave={onLeave} onClick={onClick} sx={boxSx}>
                {inner}
            </Box>
        )
    }
    return (
        <Box component="button" type={type} data-testid={testId} className="fancybutton" disabled={disabled} onClick={onClick}
            onMouseEnter={handleMouseEnter} onMouseLeave={onLeave}
            sx={{ ...boxSx, fontFamily: 'inherit' }}>
            {inner}
        </Box>
    )
}
